import React from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'secondstep-components'
import {
  MODAL_CONTENT_TYPES,
  MODAL_FLOW_TYPES,
} from 'experienceComponents/CreateInstanceShared/constants.js'
import {
  Cancel,
  Create,
  Delete,
  Edit,
  HideSmallGroup,
  AddInstructor,
  LeaveClass,
  Error,
  ErrorDetails,
  Loading,
  Success,
} from 'experienceComponents/CreateInstanceShared'

const ModalRouter = ({
  // State props
  courseInstanceName,
  inputErrors,
  isFormValid,
  sitesWithGrades, // Site-centric rename (was licensesWithGrades)
  modalContent,
  selectedCourse,
  selectedSchool,
  modalFlow,
  error,
  selectedGrade,
  // Class method props
  resetState,
  handleClassNameChange,
  handleGradeChange,
  handleSiteChange, // Site-centric rename (was handleLicenseChange)
  handleSubmit,
  handleCancel,
  handleDelete,
  handleSaveEdit,
  handleHideSmallGroup,
  handleLeaveClass,
  goBackOneModal,
  handleCreate,
  handleViewClass,
  getLessonsLink = () => {},
  limitReached,
  // Other props
  addSecondaryTeacher,
  contentfulCourses,
  currentSite,
  fetchInstructorData,
  instructors,
  isClassCardMenu,
  isShared,
  lms,
  componentType = 'course',
  redirectToMyClasses,
  registrationId,
  secondaryUserIds,
  navigationEvent,
  userContext,
  userProfile,
}) => {
  const { name: selectedCourseName } = selectedCourse || {}
  const { siteId: selectedSiteId, grades: selectedSiteGrades = [] } =
    selectedSchool || {}
  // Defensive: handle undefined/null/empty
  const userHasOneSite =
    Array.isArray(sitesWithGrades) && sitesWithGrades.length === 1
  const siteDisabled = userHasOneSite
  const modalHandleCancel = resetState
  const lessonsLink = getLessonsLink()

  let modalBody

  if (modalContent === MODAL_CONTENT_TYPES.Create) {
    modalBody = (
      <Create
        componentType={componentType}
        courseInstanceName={courseInstanceName}
        courses={selectedSiteGrades}
        currentSite={currentSite}
        dataTestId={`modal-create-${
          componentType === 'smallGroup' ? 'small-group' : 'course'
        }`}
        handleCancel={modalHandleCancel}
        handleClassNameChange={handleClassNameChange}
        handleGradeChange={e => handleGradeChange(e, contentfulCourses)}
        handleSiteChange={e =>
          handleSiteChange(
            e,
            Array.isArray(sitesWithGrades) ? sitesWithGrades : [],
          )
        }
        handleSubmit={handleSubmit}
        inputErrors={inputErrors}
        lessonsLink={lessonsLink}
        selectedCourse={selectedCourseName}
        selectedSchool={selectedSchool}
        selectedSiteId={selectedSiteId}
        siteDisabled={siteDisabled}
        sites={Array.isArray(sitesWithGrades) ? sitesWithGrades : []}
        submitDisabled={!isFormValid}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Delete) {
    modalBody = (
      <Delete
        componentType={componentType}
        courseInstanceName={courseInstanceName}
        dataTestId={'modal-delete'}
        handleCancel={handleCancel}
        handleSubmit={handleDelete}
        isClassCardMenu={isClassCardMenu}
        isShared={isShared}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.AddInstructor) {
    modalBody = (
      <AddInstructor
        addSecondaryTeacher={addSecondaryTeacher}
        currentSite={currentSite}
        dataTestId="modal-add-instructor"
        fetchInstructorData={fetchInstructorData}
        handleCancel={handleCancel}
        instructors={instructors}
        registrationId={registrationId}
        secondaryUserIds={secondaryUserIds}
        userContext={userContext}
        userProfile={userProfile}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.LeaveClass) {
    modalBody = (
      <LeaveClass
        courseInstanceName={courseInstanceName}
        dataTestId="modal-leave-class"
        handleCancel={handleCancel}
        handleSubmit={handleLeaveClass}
        isClassCardMenu={isClassCardMenu}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.HideSmallGroup) {
    modalBody = (
      <HideSmallGroup
        courseInstanceName={courseInstanceName}
        dataTestId="modal-hide-small-group"
        handleCancel={handleCancel}
        handleSubmit={handleHideSmallGroup}
        isClassCardMenu={isClassCardMenu}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Edit) {
    modalBody = (
      <Edit
        componentType={componentType}
        courseInstanceName={courseInstanceName}
        dataTestId={'modal-edit'}
        handleCancel={handleCancel}
        handleClassNameChange={handleClassNameChange}
        handleSubmit={handleSaveEdit}
        inputErrors={inputErrors}
        isClassCardMenu={isClassCardMenu}
        submitDisabled={!isFormValid}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Cancel) {
    modalBody = (
      <Cancel
        cancelFor={modalFlow}
        dataTestId={'modal-cancel'}
        handleCancel={modalHandleCancel}
        handleSubmit={goBackOneModal}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Success) {
    const submitFunc =
      modalFlow === MODAL_FLOW_TYPES.Edit
        ? resetState
        : () => {
            try {
              const selectedSiteId = selectedSchool?.siteId
              const currentSiteId = currentSite?.siteId
              const currentUrl = window.location.href
              const url = new URL(currentUrl)
              url.searchParams.get('site')
              if (selectedSiteId && selectedSiteId !== currentSiteId) {
                url.searchParams.set('site', selectedSiteId)
              }
              url.searchParams.get('site')
              // Use router navigation if provided
              const finalPath = `${url.pathname}${url.search}${url.hash}`
              try {
                // Show loading while we switch
                if (window?.appStore?.dispatch) {
                  try {
                    const {
                      operations: loadingOperations,
                    } = require('store/loadingManager')
                    window.appStore.dispatch(
                      loadingOperations.setIsLoadingState('Switching site...'),
                    )
                  } catch (_) {}
                }
                // Prefer app router: replace current URL, then reload
                if (navigationEvent) {
                  navigationEvent(finalPath, 'REPLACE')
                  window.location.reload()
                } else if (redirectToMyClasses) {
                  redirectToMyClasses(finalPath)
                  window.location.reload()
                } else {
                  window.location.href = finalPath
                }
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(
                  '[CreateInstanceShared] ModalRouter router replace+reload failed',
                  e,
                )
                window.location.href = finalPath
              } finally {
                if (window?.appStore?.dispatch) {
                  try {
                    const {
                      operations: loadingOperations,
                    } = require('store/loadingManager')
                    window.appStore.dispatch(
                      loadingOperations.setNotLoadingState(),
                    )
                  } catch (_) {}
                }
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('[CreateInstanceShared] URL update failed', e)
              handleViewClass()
            }
          }
    const cancelFunc =
      modalFlow === MODAL_FLOW_TYPES.Create ? handleCreate : resetState
    modalBody = (
      <Success
        currentSite={currentSite}
        dataTestId={'modal-success'}
        debugContext={{ page: 'ModalRouter' }}
        handleCancel={cancelFunc}
        handleClose={resetState}
        handleSubmit={submitFunc}
        maxClassesReached={limitReached}
        navigationEvent={navigationEvent}
        selectedSchool={selectedSchool}
        successFor={modalFlow}
        title={selectedGrade}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Error) {
    let submitFunc = handleSubmit
    if (modalFlow === MODAL_FLOW_TYPES.Edit) {
      submitFunc = handleSaveEdit
    } else if (modalFlow === MODAL_FLOW_TYPES.Delete) {
      submitFunc = handleDelete
    } else if (modalFlow === MODAL_FLOW_TYPES.HideSmallGroup) {
      submitFunc = handleHideSmallGroup
    }
    modalBody = (
      <Error
        dataTestId={'modal-error'}
        error={error || lms.error}
        errorFor={modalFlow}
        handleCancel={modalHandleCancel}
        handleSubmit={submitFunc}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.ErrorDetails) {
    modalBody = (
      <ErrorDetails
        dataTestId={'modal-error-details'}
        error={error || lms.error}
        errorFor={modalFlow}
        handleCancel={modalHandleCancel}
        handleSubmit={modalHandleCancel}
      />
    )
  } else if (modalContent === MODAL_CONTENT_TYPES.Loading) {
    modalBody = <Loading dataTestId={'modal-loading'} />
  }

  if (!modalBody) {
    return null
  }

  return (
    <Modal
      dataTestId={`modal-create-${
        componentType === 'smallGroup' ? 'small-group' : 'course'
      }`}
      isModalOpen={!!modalBody}
      modalClose={modalHandleCancel}
    >
      {modalBody}
    </Modal>
  )
}

ModalRouter.propTypes = {
  addSecondaryTeacher: PropTypes.func,
  componentType: PropTypes.oneOf(['course', 'smallGroup']),
  // Other props
  contentfulCourses: PropTypes.array,
  // State props
  courseInstanceName: PropTypes.string,
  currentSite: PropTypes.object,
  error: PropTypes.string,
  fetchInstructorData: PropTypes.func,
  getLessonsLink: PropTypes.func.isRequired,
  goBackOneModal: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleClassNameChange: PropTypes.func.isRequired,
  handleCreate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleGradeChange: PropTypes.func.isRequired,
  handleHideSmallGroup: PropTypes.func,
  handleLeaveClass: PropTypes.func,
  handleSaveEdit: PropTypes.func.isRequired,
  handleSiteChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleViewClass: PropTypes.func.isRequired,
  inputErrors: PropTypes.object,
  instructors: PropTypes.array,
  isClassCardMenu: PropTypes.bool,
  isFormValid: PropTypes.bool,
  isShared: PropTypes.bool,
  limitReached: PropTypes.bool,
  lms: PropTypes.object,
  modalContent: PropTypes.string,
  modalFlow: PropTypes.string,
  navigationEvent: PropTypes.func,
  // Class method props
  redirectToMyClasses: PropTypes.func,
  registrationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resetState: PropTypes.func.isRequired,
  secondaryUserIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  selectedCourse: PropTypes.object,
  selectedGrade: PropTypes.string,
  selectedSchool: PropTypes.object,
  sitesWithGrades: PropTypes.array,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

export default ModalRouter
